<template>
  <div class="main-container">
    <h2 class="page-title">유저 초대</h2>

    <form class="invite-form" @submit.prevent="createUser">
      <!-- 이름 -->
      <UiInput placeholder="이름" v-model="form.name" autocomplete="name" />

      <!-- 전화번호 -->
      <UiInput
        placeholder="전화번호 (예: 01012345678)"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        v-model="form.phone"
      />

      <!-- 특이사항 (users.admin_note): 회원관리·신규회원 목록과 회원 상세에 늘 보이는 칸이다.
           일반 메모(소개 경로 등)는 회원 상세의 메모·연락 기록에 남긴다 -->
      <textarea
        class="ui-textarea"
        placeholder="특이사항 (부상·주의사항 등, 코치가 늘 알아야 할 내용)"
        aria-label="특이사항"
        v-model="form.note"
        rows="3"
      ></textarea>

      <div class="button-group">
        <UiButton
          type="submit"
          :disabled="!canSubmit || loading"
          :loading="loading"
        >
          유저 생성하기
        </UiButton>

        <UiButton
          type="button"
          @click="sendInviteSMS"
          :disabled="!canSendSMS || smsLoading"
          :loading="smsLoading"
        >
          초대 문자 보내기
        </UiButton>
      </div>
    </form>

    <!-- 성공 메시지 -->
    <div v-if="successMessage" class="success-message">
      {{ successMessage }}
    </div>
  </div>
</template>

<script>
import { api } from "@/lib/api";
import UiButton from "@/components/UiButton.vue";
import UiInput from "@/components/UiInput.vue";

export default {
  name: "InviteView",
  components: {
    UiButton,
    UiInput,
  },
  data() {
    return {
      loading: false,
      smsLoading: false,
      createdUserId: null,
      successMessage: "",
      form: {
        name: "",
        phone: "",
        note: "",
      },
    };
  },
  computed: {
    phoneValid() {
      const digits = (this.form.phone || "").replace(/\D/g, "");
      return digits.length >= 10 && digits.length <= 11;
    },
    canSubmit() {
      const { name, phone } = this.form;
      return !!name && !!phone && this.phoneValid;
    },
    canSendSMS() {
      // ✅ 전화번호만 있으면 초대 문자 보내기 가능
      return !!this.form.phone && this.phoneValid;
    },
  },
  methods: {
    async createUser() {
      if (!this.canSubmit || this.loading) return;
      this.loading = true;
      this.successMessage = "";

      try {
        const res = await api("/v1/admin/users", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: this.form.name,
            phone_number: this.form.phone.replace(/\D/g, ""),
            // 앞뒤 공백·빈 줄은 뺀다(Enter만 친 칸이 회원 상세에 빈 특이사항 칸으로 보였다, 4차 리뷰)
            admin_note: this.form.note.trim(),
          }),
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.message || "유저 생성 실패");
        }

        const data = await res.json();
        this.createdUserId = data.user_id || data.id;

        this.successMessage = "✅ 유저가 생성되었습니다!";

        // 폼 리셋 (전화번호는 유지 - 초대 문자 보낼 수 있게)
        this.form.name = "";
        this.form.note = "";
      } catch (e) {
        console.error(e);
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
      } finally {
        this.loading = false;
      }
    },

    async sendInviteSMS() {
      if (!this.canSendSMS || this.smsLoading) return;
      this.smsLoading = true;
      this.successMessage = "";

      try {
        const res = await api("/v1/admin/invitations/sms", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone_number: this.form.phone.replace(/\D/g, ""),
          }),
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.message || "초대 문자 전송 실패");
        }

        this.successMessage = "✅ 초대 문자가 전송되었습니다!";

        // 폼 완전 리셋
        this.form.name = "";
        this.form.phone = "";
        this.form.note = "";
        this.createdUserId = null;
      } catch (e) {
        console.error(e);
        // 에러는 이미 handleApiError에서 처리되므로 여기서는 추가 처리하지 않음
      } finally {
        this.smsLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.main-container {
  background: #151718;
  border-radius: 10px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 400px;
}

.page-title {
  margin: 0 0 24px 0;
  font-size: 24px;
  font-weight: 400;
  color: #fff;
}

/* 폼 */
.invite-form {
  display: grid;
  gap: 16px;
  max-width: 400px;
}

.invite-form :deep(.ui-input) {
  height: 54px;
  border-radius: 5px;
  border: 1px solid rgba(114, 114, 114, 0.2);
  background: #0a0a0a;
  color: #fff;
  padding: 0 14px;
}

.invite-form :deep(.ui-input::placeholder) {
  color: rgba(255, 255, 255, 0.6);
}

.invite-form :deep(.ui-input:focus) {
  outline: none;
  border-color: #49de80;
  box-shadow: 0 0 0 2px rgba(73, 222, 128, 0.25);
}

/* textarea 스타일 */
.ui-textarea {
  border-radius: 5px;
  border: 1px solid rgba(114, 114, 114, 0.2);
  background: #0a0a0a;
  color: #fff;
  padding: 14px;
  font-family: inherit;
  font-size: 16px;
  resize: vertical;
  min-height: 80px;
  /* 회원 상세 특이사항 칸처럼 한국어 낱말 중간에서 줄을 바꾸지 않는다(띄어쓰기 없는 긴 글은 어디서든 끊는다, 5차 리뷰) */
  word-break: keep-all;
  overflow-wrap: anywhere;
}

.ui-textarea::placeholder {
  color: rgba(255, 255, 255, 0.6);
}

.ui-textarea:focus {
  outline: none;
  border-color: #49de80;
  box-shadow: 0 0 0 2px rgba(73, 222, 128, 0.25);
}

/* ✅ 버튼 그룹 */
.button-group {
  display: grid;
  gap: 12px;
}

.button-group .ui-btn {
  width: 100%;
}

/* 성공 메시지 */
.success-message {
  margin-top: 12px;
  padding: 12px;
  background: rgba(73, 222, 128, 0.1);
  border: 1px solid #49de80;
  border-radius: 5px;
  color: #49de80;
  font-size: 16px;
  font-weight: 600;
  text-align: center;
  max-width: 400px;
}
</style>
